import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { platformAdminAPI } from '../../services/api';

export default function TenantsList() {
  const [tenants, setTenants] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [statsRes, tenantsRes] = await Promise.all([
        platformAdminAPI.getStats(),
        platformAdminAPI.listTenants()
      ]);
      setStats(statsRes.data || statsRes);
      setTenants(tenantsRes.data || []);
    } catch (err) {
      setError('Failed to load tenants: ' + (err.response?.data?.error || err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (tenant) => {
    const newStatus = tenant.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE';
    try {
      setTogglingId(tenant.id);
      setError(null);
      await platformAdminAPI.updateTenantStatus(tenant.id, newStatus);
      await loadData();
    } catch (err) {
      setError('Failed to update status: ' + (err.response?.data?.error || err.message || 'Unknown error'));
    } finally {
      setTogglingId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return <div className="loading">Loading tenants...</div>;
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <h1>Platform Tenants</h1>
        <Link to="/admin" className="btn btn-secondary">Back to Admin</Link>
      </div>

      {error && (
        <div style={{ padding: '0.75rem 1rem', marginBottom: '1rem', borderRadius: '6px', background: 'rgba(139,58,58,0.1)', border: '1px solid #8B3A3A', color: '#8B3A3A', fontSize: '0.875rem' }}>
          {error}
          <button className="btn btn-sm btn-secondary" style={{ marginLeft: '1rem' }} onClick={loadData}>Retry</button>
        </div>
      )}

      {/* Stat cards */}
      {stats && (
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
          <div className="form-section" style={{ flex: '1 1 200px', marginBottom: 0 }}>
            <h2 style={{ fontSize: '0.875rem', margin: 0, opacity: 0.6 }}>Total Tenants</h2>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, marginTop: '0.25rem' }}>
              {stats.totalTenants ?? 0}
            </div>
            <span className="text-muted" style={{ fontSize: '0.75rem' }}>{stats.activeTenants ?? 0} active</span>
          </div>
          <div className="form-section" style={{ flex: '1 1 200px', marginBottom: 0 }}>
            <h2 style={{ fontSize: '0.875rem', margin: 0, opacity: 0.6 }}>Active Users</h2>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, marginTop: '0.25rem' }}>
              {stats.activeUsers ?? 0}
            </div>
            <span className="text-muted" style={{ fontSize: '0.75rem' }}>{stats.totalUsers ?? 0} total</span>
          </div>
          <div className="form-section" style={{ flex: '1 1 200px', marginBottom: 0 }}>
            <h2 style={{ fontSize: '0.875rem', margin: 0, opacity: 0.6 }}>Quotes This Period</h2>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, marginTop: '0.25rem' }}>
              {stats.totalQuotesThisPeriod ?? 0}
            </div>
          </div>
          <div className="form-section" style={{ flex: '1 1 200px', marginBottom: 0 }}>
            <h2 style={{ fontSize: '0.875rem', margin: 0, opacity: 0.6 }}>New Tenants (30d)</h2>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, marginTop: '0.25rem' }}>
              {stats.newTenantsLast30d ?? 0}
            </div>
          </div>
        </div>
      )}

      {/* Tenants table */}
      <div className="form-section">
        <table className="simple-table">
          <thead>
            <tr>
              <th>Company</th>
              <th>Plan</th>
              <th>Status</th>
              <th>Usage</th>
              <th>Users</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tenants.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: '2rem' }} className="text-muted">
                  No tenants yet. V2 customers will appear here when they sign up.
                </td>
              </tr>
            ) : (
              tenants.map(tenant => (
                <tr key={tenant.id}>
                  <td>
                    <div style={{ fontWeight: 500 }}>{tenant.name}</div>
                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>{tenant.company_email || tenant.slug}</div>
                  </td>
                  <td>{tenant.custom_plan_name || tenant.plan_tier || '-'}</td>
                  <td>
                    <button
                      className="btn btn-sm"
                      style={{
                        background: tenant.status === 'ACTIVE' ? 'rgba(92,122,84,0.15)' : 'rgba(139,58,58,0.15)',
                        color: tenant.status === 'ACTIVE' ? '#5C7A54' : '#8B3A3A',
                        border: 'none',
                        cursor: 'pointer',
                        minWidth: '80px'
                      }}
                      disabled={togglingId === tenant.id}
                      onClick={() => handleToggleStatus(tenant)}
                    >
                      {togglingId === tenant.id ? '...' : tenant.status}
                    </button>
                  </td>
                  <td>{tenant.current_period_usage ?? 0}/{tenant.quote_limit ?? 0} quotes</td>
                  <td>{tenant.user_count ?? 0}/{tenant.seat_limit ?? 0} seats</td>
                  <td className="text-muted" style={{ fontSize: '0.8rem' }}>{formatDate(tenant.created_at)}</td>
                  <td>
                    <Link to={`/admin/tenants/${tenant.id}`} className="btn btn-sm btn-secondary">View</Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
